import React from "react";
import moment from "moment";
import { Icon, Skeleton } from "antd";
import styled from "styled-components";

import { toBRL } from "~/components/UI/InputCurrency";
import { filterInstallmentsByMonth, formatPeriodLabel } from "./ParcelasIndicator";

/**
 * Barra de KPIs do painel financeiro. Mesmo visual dos cards de resumo já
 * usados na Conciliação Bancária (CardsRow + SummaryCard), porém com métricas
 * focadas no que importa aqui: faturamento dos serviços extras, quanto já foi
 * recebido, quanto está em aberto e — o destaque — quantos tickets já
 * concluídos ainda têm parcela em aberto.
 *
 * As métricas são derivadas das mesmas tarefas carregadas no board
 * (state.dashTasks), via item.payment.installments.
 */

var PeriodHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #8c8c8c;
  padding-top: 10px;
  margin-bottom: 2px;
`;

var PeriodValue = styled.span`
  font-weight: 600;
  color: #595959;
`;

var CardsRow = styled.div`
  display: flex;
  gap: 16px;
  padding-top: 6px;
  margin-bottom: 16px;
`;

var CardWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

var SummaryCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  border: 1px solid #e8e8e8;
  transition: all 0.2s ease;
  height: 100%;
  min-height: 105px;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

var SummaryCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

var SummaryCardIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${function(props) { return props.bgColor || "#f0f0f0"; }};
  color: ${function(props) { return props.color || "#666"; }};
  font-size: 16px;
`;

var SummaryCardTitle = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 500;
`;

var SummaryCardValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${function(props) { return props.color || "rgba(0, 0, 0, 0.85)"; }};
  line-height: 1.2;
`;

var SummaryCardSuffix = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.45);
  margin-left: 6px;
`;

var isPaid = function(installment) {
  if (!installment) {
    return false;
  }
  if (installment.status === "Pago") {
    return true;
  }
  return installment.paid_at !== null && installment.paid_at !== undefined && installment.paid_at !== "";
};

var aggregate = function(list, month) {
  var columns = ["not_started", "in_process", "done"];
  var billed = 0;
  var received = 0;
  var open = 0;
  var doneUnpaidCount = 0;
  var doneUnpaidAmount = 0;

  columns.forEach(function(col) {
    var items = (list && list[col]) || [];
    items.forEach(function(item) {
      var all = item.payment && Array.isArray(item.payment.installments) ? item.payment.installments : [];
      var installments = filterInstallmentsByMonth(all, month);
      var hasOpen = false;

      installments.forEach(function(p) {
        var amount = parseFloat(p.amount);
        if (isNaN(amount)) {
          amount = 0;
        }
        billed += amount;
        if (isPaid(p)) {
          received += amount;
        } else {
          open += amount;
          hasOpen = true;
        }
      });

      if (col === "done" && installments.length > 0 && hasOpen) {
        doneUnpaidCount++;
        installments.forEach(function(p) {
          if (!isPaid(p)) {
            var a = parseFloat(p.amount);
            if (isNaN(a)) {
              a = 0;
            }
            doneUnpaidAmount += a;
          }
        });
      }
    });
  });

  var rate = billed > 0 ? Math.round((received / billed) * 100) : 0;
  return { billed: billed, received: received, open: open, doneUnpaidCount: doneUnpaidCount, doneUnpaidAmount: doneUnpaidAmount, rate: rate };
};

var money = function(value) {
  return "R$ " + toBRL(value || 0);
};

export var FinanceiroStats = function(props) {
  var stats = aggregate(props.list, props.month);
  var isLoading = props.isLoading;

  var cards = [
    {
      key: "billed",
      title: "Faturado",
      icon: "dollar",
      bg: "#e6f7ff",
      color: "#1890ff",
      value: money(stats.billed),
    },
    {
      key: "received",
      title: "Recebido",
      icon: "check-circle",
      bg: "#f6ffed",
      color: "#52c41a",
      value: money(stats.received),
      suffix: stats.rate + "%",
    },
    {
      key: "open",
      title: "Em aberto",
      icon: "clock-circle",
      bg: "#fffbe6",
      color: "#faad14",
      value: money(stats.open),
    },
    {
      key: "done_unpaid",
      title: "Concluídos não pagos",
      icon: "exclamation-circle",
      bg: "#fff1f0",
      color: "#f5222d",
      value: String(stats.doneUnpaidCount),
      suffix: money(stats.doneUnpaidAmount) + " em aberto",
    },
  ];

  var periodLabel = formatPeriodLabel(props.month, true);
  periodLabel = periodLabel.charAt(0).toUpperCase() + periodLabel.slice(1);

  return (
    <div>
    <PeriodHeader>
      <Icon type="calendar" />
      Resumo de <PeriodValue>{periodLabel}</PeriodValue>
    </PeriodHeader>
    <CardsRow>
      {cards.map(function(card) {
        return (
          <CardWrapper key={card.key}>
            <SummaryCard>
              {isLoading ? (
                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
              ) : (
                <>
                  <SummaryCardHeader>
                    <SummaryCardIcon bgColor={card.bg} color={card.color}>
                      <Icon type={card.icon} />
                    </SummaryCardIcon>
                    <SummaryCardTitle>{card.title}</SummaryCardTitle>
                  </SummaryCardHeader>
                  <SummaryCardValue color={card.color}>
                    {card.value}
                    {card.suffix && <SummaryCardSuffix>{card.suffix}</SummaryCardSuffix>}
                  </SummaryCardValue>
                </>
              )}
            </SummaryCard>
          </CardWrapper>
        );
      })}
    </CardsRow>
    </div>
  );
};

export default React.memo(FinanceiroStats);
